import { ContentType } from '@medplum/core';
import type { MedplumClient } from '@medplum/core';
import type { Identifier } from '@medplum/fhirtypes';
import type { SignatureInfo } from '../pages/appointments/fhirNoteMapping';

export const PROVIDER_ONBOARDING_COMPLETION_BOT_IDENTIFIER = {
  system: 'https://unify.health/bots',
  value: 'provider-onboarding-completion',
} as const;

export const PROVIDER_ONBOARDING_SECTION_BOT_IDENTIFIER = {
  system: 'https://unify.health/bots',
  value: 'provider-onboarding-section',
} as const;

export type ProviderOnboardingSectionId =
  | 'personal-info'
  | 'availability'
  | 'profile'
  | 'licenses-education'
  | 'attestation';

export interface ProviderOnboardingCompletionSuccess {
  ok: true;
  practitionerId: string;
  attestedAt: string;
}

export interface ProviderOnboardingCompletionFailure {
  ok: false;
  message: string;
  missingSections: Array<{
    sectionId: ProviderOnboardingSectionId;
    title: string;
    missingFields: string[];
  }>;
}

export type ProviderOnboardingCompletionResult =
  | ProviderOnboardingCompletionSuccess
  | ProviderOnboardingCompletionFailure;

function getProviderOnboardingBotTarget(): string | Identifier {
  const id = import.meta.env.VITE_MEDPLUM_PROVIDER_ONBOARDING_COMPLETION_BOT_ID?.trim();
  if (id) {
    return id;
  }

  const system =
    import.meta.env.VITE_MEDPLUM_PROVIDER_ONBOARDING_COMPLETION_BOT_IDENTIFIER_SYSTEM?.trim()
    ?? PROVIDER_ONBOARDING_COMPLETION_BOT_IDENTIFIER.system;
  const value =
    import.meta.env.VITE_MEDPLUM_PROVIDER_ONBOARDING_COMPLETION_BOT_IDENTIFIER_VALUE?.trim()
    ?? PROVIDER_ONBOARDING_COMPLETION_BOT_IDENTIFIER.value;

  return { system, value };
}

function getProviderOnboardingSectionBotTarget(): string | Identifier {
  const id = import.meta.env.VITE_MEDPLUM_PROVIDER_ONBOARDING_SECTION_BOT_ID?.trim();
  if (id) {
    return id;
  }

  const system =
    import.meta.env.VITE_MEDPLUM_PROVIDER_ONBOARDING_SECTION_BOT_IDENTIFIER_SYSTEM?.trim()
    ?? PROVIDER_ONBOARDING_SECTION_BOT_IDENTIFIER.system;
  const value =
    import.meta.env.VITE_MEDPLUM_PROVIDER_ONBOARDING_SECTION_BOT_IDENTIFIER_VALUE?.trim()
    ?? PROVIDER_ONBOARDING_SECTION_BOT_IDENTIFIER.value;

  return { system, value };
}

export type ProviderOnboardingCompletableSectionId =
  | 'personal-info'
  | 'availability'
  | 'profile'
  | 'licenses-education';

export interface ProviderOnboardingSectionCompleteSuccess {
  ok: true;
}

export type ProviderOnboardingSectionCompletionResult =
  | ProviderOnboardingSectionCompleteSuccess
  | ProviderOnboardingCompletionFailure;

export async function completeProviderOnboardingSection(
  medplum: MedplumClient,
  sectionId: ProviderOnboardingCompletableSectionId,
): Promise<ProviderOnboardingSectionCompletionResult> {
  const ensured = await medplum.executeBot(
    getProviderOnboardingBotTarget(),
    { operation: 'ensure-section-tasks' },
    ContentType.JSON,
  ) as ProviderOnboardingSectionCompletionResult;
  if (!ensured.ok) {
    return ensured;
  }
  return medplum.executeBot(
    getProviderOnboardingSectionBotTarget(),
    { operation: 'complete-section', sectionId },
    ContentType.JSON,
  ) as Promise<ProviderOnboardingSectionCompletionResult>;
}

export async function completeProviderOnboarding(
  medplum: MedplumClient,
  signature: SignatureInfo,
): Promise<ProviderOnboardingCompletionResult> {
  return medplum.executeBot(
    getProviderOnboardingBotTarget(),
    { signature },
    ContentType.JSON,
  ) as Promise<ProviderOnboardingCompletionResult>;
}
